/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PHONE_NUMBER } from "@utils/expreciones";
import { useFormik } from "formik";
import * as yup from "yup";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import React, { useEffect, useState } from "react";
import {
  useLazyGetCivilStatusQuery,
  useLazyGetMatrimonialRegimesQuery,
  useLazyGetPersonTypeQuery,
} from "@api/catalogs";
import {
  CatalogPersonType,
  CivilStatus,
  MatrimonialRegimesData,
  PayloadPersonPatch,
  PersonaStructure,
} from "@interfaces/index";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetPersonsQuery, usePatchPersonMutation, usePostPersonMutation } from "@api/personasApi";
import { useNavigate } from "react-router-dom";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import { PERSON_CATALOG } from "@helpers/constantes";
import * as Sentry from "@sentry/react";
import { UUID } from "node:crypto";

const OTHER_PERSON_ID = "1-2-3-4-5";

const InformacionAval = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const personAval = useSelector((state: RootState) => state.bofu.aval);

  const [getEstadoCivil] = useLazyGetCivilStatusQuery();
  const [getMatrimonialRegimen] = useLazyGetMatrimonialRegimesQuery();
  const [patchPerson] = usePatchPersonMutation();
  const [postPerson] = usePostPersonMutation();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getPersonType] = useLazyGetPersonTypeQuery();

  const [listEdoCivil, setListEdoCivil] = useState<CivilStatus[]>([]);
  const [listMatrimonialRegimen, setListMatrimonialRegimen] = useState<MatrimonialRegimesData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [idEdoCivilCasado, setIdEdoCivilCasado] = useState<string>("");
  const [personTypeConyugeAval, setPersonTypeConyugeAval] = useState<CatalogPersonType | null>(null);
  const [avalSelected, setAvalSelected] = useState<PersonaStructure | null>(null);
  const [otherPersons, setOtherPersons] = useState<PersonaStructure[]>([]);

  const formik = useFormik({
    initialValues: {
      ocupacion: "",
      country_code: "+52",
      phone: "",
      estado_civil_id: "",
      matrimonial_regimes_id: "",
      conyuge_id: "",
      conyuge_name: "",
      conyuge_last_name: "",
      conyuge_last_name_2: "",
    },
    validationSchema: yup.object().shape({
      country_code: yup.string().required("Campo requerido"),
      phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
      ocupacion: yup.string().required("Campo requerido"),
      estado_civil_id: yup.string().required("Campo requerido"),
      matrimonial_regimes_id: yup.string().when("estado_civil_id", {
        is: idEdoCivilCasado,
        then: (schema) => schema.required("Campo requerido"),
      }),
      conyuge_id: yup.string().when("estado_civil_id", {
        is: idEdoCivilCasado,
        then: (schema) => schema.required("Campo requerido"),
      }),
      conyuge_name: yup.string().when("conyuge_id", {
        is: OTHER_PERSON_ID,
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      }),
      conyuge_last_name: yup.string().when("conyuge_id", {
        is: OTHER_PERSON_ID,
        then: (schema) => schema.required("Campo requerido"),
      }),
      conyuge_last_name_2: yup.string(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!avalSelected || !personTypeConyugeAval) {
          return;
        }

        setSubmitting(true);

        const list_persons = await getPersons({
          loanApplicationId: applicationData.id,
        }).unwrap();

        const is_married = values.estado_civil_id === idEdoCivilCasado;
        let person_conyuge: PersonaStructure | null = null;

        if (is_married) {
          // si es casado, se valida si se selecciono otra persona, si es asi, se crea un nuevo conyuge
          if (values.conyuge_id === OTHER_PERSON_ID) {
            const response_create = await postPerson({
              body: {
                loan_application_id: applicationData.id,
                person_types: [personTypeConyugeAval.id],
                name: values.conyuge_name,
                last_name: values.conyuge_last_name,
                last_name_2: values.conyuge_last_name_2,
              },
            }).unwrap();
            person_conyuge = response_create.data;
          } else {
            // obtener la informacion del conyuge
            person_conyuge = list_persons.find((item) => item.id === values.conyuge_id) || null;
            if (!person_conyuge) {
              throw new Error("No se encontró el conyuge");
            }

            // obtener los tipos de persona del conyuge
            const person_types = person_conyuge.person_types_detail.map((item) => item.id);

            // validar si el conyuge tiene el tipo de persona 'conyuge aval', en caso de no tenerlo, se agrega
            if (!person_types.includes(personTypeConyugeAval.id as UUID)) {
              await patchPerson({
                personId: person_conyuge.id,
                body: {
                  person_types: [...person_types, personTypeConyugeAval.id],
                },
              }).unwrap();
            }
          }
        }

        const payload: PayloadPersonPatch = {
          occupation: values.ocupacion,
          code_phone_number: values.country_code,
          phone_number: values.phone,
          civil_status_id: values.estado_civil_id,
          matrimonial_regimes_id: null,
          spouse_id: null,
        };

        if (is_married) {
          if (!person_conyuge) {
            throw new Error("No se encontró el conyuge");
          }
          payload.matrimonial_regimes_id = values.matrimonial_regimes_id;
          payload.spouse_id = person_conyuge.id;
        }

        await patchPerson({
          personId: avalSelected.id,
          body: payload,
        }).unwrap();

        navigate("/producto/creditosimple/informacion_datos_aval_2");
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionAval-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const savePersonalInformation = async (
    e:
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
      | React.SyntheticEvent<Element, Event>,
  ) => {
    try {
      formik.handleBlur(e);

      if (!avalSelected) {
        return;
      }

      const { values, errors } = formik;

      const { country_code, phone, ocupacion, estado_civil_id, matrimonial_regimes_id } = errors;
      if (country_code || phone || ocupacion || estado_civil_id || matrimonial_regimes_id) {
        return;
      }

      const payload: PayloadPersonPatch = {
        occupation: values.ocupacion,
        code_phone_number: values.country_code,
        phone_number: values.phone,
        civil_status_id: values.estado_civil_id,
        matrimonial_regimes_id: values.estado_civil_id === idEdoCivilCasado ? values.matrimonial_regimes_id : null,
      };

      await patchPerson({
        personId: avalSelected?.id,
        body: payload,
      }).unwrap();
    } catch (error) {
      console.error("Error guardando información personal:", error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        if (!personAval) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        const [res_persons, res_edo_civil, res_edo_matrimonial, res_person_type] = await Promise.all([
          getPersons({
            loanApplicationId: applicationData.id,
            // id: personAval?.id,
          }).unwrap(),
          getEstadoCivil().unwrap(),
          getMatrimonialRegimen().unwrap(),
          getPersonType().unwrap(),
        ]);

        // obtener el aval seleccionado, con la informacion actualizada
        const aval_selected = res_persons.find((item) => item.id === personAval.id);

        // validar si el aval seleccionado existe
        if (!aval_selected) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        const other_persons = [];
        const conyuges_persons = [];

        // obtener las personas diferentes al aval seleccionado y los conyuges
        for (const person of res_persons) {
          if (person.id !== personAval.id) {
            other_persons.push(person);

            const person_types = person.person_types_detail.map((item) => item.code);
            if (person_types.includes(PERSON_CATALOG.conyuge_aval.code)) {
              conyuges_persons.push(person);
            }
          }
        }

        // agregar el conyugue "otros" a la lista de personas, este sirve para crear un nuevo conyuge
        const other_person = {
          id: OTHER_PERSON_ID,
          name: "Otro",
          last_name: "",
          last_name_2: "",
          person_types_detail: [],
        } as unknown as PersonaStructure;

        other_persons.push(other_person);

        // buscar id para el estado civil "casado"
        const edo_civil_casado: CivilStatus | undefined = res_edo_civil.find(
          (item) => item.name.toLowerCase() === "casado",
        );

        // buscar el id para el tipo de persona "conyuge aval"
        const person_type_conyuge_aval = res_person_type.find((item) => item.code === PERSON_CATALOG.conyuge_aval.code);

        if (!edo_civil_casado || !person_type_conyuge_aval) {
          throw new Error("No se encontraron los datos necesarios para continuar.");
        }

        setIdEdoCivilCasado(edo_civil_casado.id);
        setPersonTypeConyugeAval(person_type_conyuge_aval);
        setListEdoCivil(res_edo_civil);
        setListMatrimonialRegimen(res_edo_matrimonial);
        setAvalSelected(aval_selected);
        setOtherPersons(other_persons);
        setAvalSelected(aval_selected);

        formik.setFieldValue("country_code", aval_selected.code_phone_number || "+52");
        formik.setFieldValue("phone", aval_selected.phone_number || "");
        formik.setFieldValue("ocupacion", aval_selected.occupation || "");
        formik.setFieldValue("estado_civil_id", aval_selected.civil_status_id || "");
        formik.setFieldValue("matrimonial_regimes_id", aval_selected.matrimonial_regimes_id || "");

        // si es casado y se encuentra una persona de tipo "conyuge aval"
        if (aval_selected.civil_status_id === edo_civil_casado.id && aval_selected.spouse_id) {
          formik.setFieldValue("conyuge_id", aval_selected.spouse_id);
        } else {
          formik.setFieldValue("conyuge_id", "");
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionAval-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información del aval
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
          >
            <strong>1</strong> de 2
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            {avalSelected?.name} {avalSelected?.last_name} {avalSelected?.last_name_2}
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <Grid container spacing={1} mt={1}>
              <Grid item xs={4} sm={4} md={4}>
                <CustomContryCode
                  label="Código"
                  name="country_code"
                  value={formik.values.country_code}
                  onChange={formik.handleChange}
                  onBlur={(e) => savePersonalInformation(e)}
                  error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={8} sm={8} md={8}>
                <TextField
                  type="text"
                  name="phone"
                  variant="outlined"
                  label="Teléfono"
                  fullWidth
                  required
                  value={formik.values.phone}
                  onChange={(event) => {
                    formik.setFieldValue("phone", event.target.value.replace(/[^0-9]/g, ""));
                  }}
                  onBlur={(e) => savePersonalInformation(e)}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                  }}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>
            </Grid>

            <TextField
              id="ocupacion"
              name="ocupacion"
              label="Ocupación"
              margin="normal"
              required
              fullWidth
              autoComplete="off"
              onBlur={(e) => savePersonalInformation(e)}
              onChange={formik.handleChange}
              error={formik.touched.ocupacion && Boolean(formik.errors.ocupacion)}
              helperText={formik.touched.ocupacion && formik.errors.ocupacion}
              value={formik.values.ocupacion}
              disabled={isLoading || formik.isSubmitting}
            />

            <FormControl
              fullWidth
              required
              sx={{
                ...customStylesAsterisk,
                mt: 2,
              }}
              error={formik.touched.estado_civil_id && Boolean(formik.errors.estado_civil_id)}
            >
              <InputLabel id="estado_civil_id">Estado civil</InputLabel>
              <Select
                labelId="estado_civil_id"
                id="estado_civil_id"
                name="estado_civil_id"
                fullWidth
                required
                label="Estado civil"
                value={formik.values.estado_civil_id}
                onBlur={(e) => savePersonalInformation(e)}
                onChange={formik.handleChange}
                error={formik.touched.estado_civil_id && Boolean(formik.errors.estado_civil_id)}
                disabled={isLoading || formik.isSubmitting}
              >
                <MenuItem value={""}>Selecciona</MenuItem>
                {listEdoCivil.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.estado_civil_id && formik.errors.estado_civil_id && (
                <FormHelperText>{formik.errors.estado_civil_id}</FormHelperText>
              )}
            </FormControl>

            {formik.values.estado_civil_id !== "" && formik.values.estado_civil_id === idEdoCivilCasado && (
              <React.Fragment>
                <FormControl
                  fullWidth
                  required
                  sx={{
                    ...customStylesAsterisk,
                    mt: 2,
                  }}
                  error={formik.touched.matrimonial_regimes_id && Boolean(formik.errors.matrimonial_regimes_id)}
                >
                  <InputLabel id="matrimonial_regimes_id">Régimen matrimonial</InputLabel>
                  <Select
                    labelId="matrimonial_regimes_id"
                    id="matrimonial_regimes_id"
                    name="matrimonial_regimes_id"
                    fullWidth
                    required
                    label="Régimen matrimonial *"
                    value={formik.values.matrimonial_regimes_id}
                    onBlur={(e) => savePersonalInformation(e)}
                    onChange={formik.handleChange}
                    error={formik.touched.matrimonial_regimes_id && Boolean(formik.errors.matrimonial_regimes_id)}
                    disabled={isLoading || formik.isSubmitting}
                  >
                    <MenuItem value={""}>Selecciona</MenuItem>
                    {listMatrimonialRegimen.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.matrimonial_regimes_id && formik.errors.matrimonial_regimes_id && (
                    <FormHelperText>{formik.errors.matrimonial_regimes_id}</FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  fullWidth
                  required
                  sx={{
                    ...customStylesAsterisk,
                    mt: 2,
                  }}
                  error={formik.touched.conyuge_id && Boolean(formik.errors.conyuge_id)}
                >
                  <InputLabel id="conyuge_id">Conyuge</InputLabel>
                  <Select
                    labelId="conyuge_id"
                    id="conyuge_id"
                    name="conyuge_id"
                    fullWidth
                    required
                    label="Conyuge"
                    value={formik.values.conyuge_id}
                    onBlur={(e) => savePersonalInformation(e)}
                    onChange={formik.handleChange}
                    error={formik.touched.conyuge_id && Boolean(formik.errors.conyuge_id)}
                    disabled={isLoading || formik.isSubmitting}
                  >
                    <MenuItem value={""}>Selecciona</MenuItem>
                    {otherPersons.map((item: PersonaStructure) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name} {item.last_name} {item.last_name_2}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.conyuge_id && formik.errors.conyuge_id && (
                    <FormHelperText>{formik.errors.conyuge_id}</FormHelperText>
                  )}
                </FormControl>

                {formik.values.conyuge_id !== "" && formik.values.conyuge_id === OTHER_PERSON_ID && (
                  <React.Fragment>
                    <FormControl fullWidth>
                      <TextField
                        id="conyuge_name"
                        name="conyuge_name"
                        label="Nombre del conyuge"
                        margin="normal"
                        required
                        fullWidth
                        autoComplete="off"
                        onBlur={(e) => savePersonalInformation(e)}
                        onChange={formik.handleChange}
                        error={formik.touched.conyuge_name && Boolean(formik.errors.conyuge_name)}
                        helperText={formik.touched.conyuge_name && formik.errors.conyuge_name}
                        value={formik.values.conyuge_name}
                        disabled={isLoading || formik.isSubmitting}
                      />
                    </FormControl>

                    <FormControl fullWidth>
                      <TextField
                        id="conyuge_last_name"
                        name="conyuge_last_name"
                        label="Apellido paterno del conyuge"
                        margin="normal"
                        required
                        fullWidth
                        autoComplete="off"
                        onBlur={(e) => savePersonalInformation(e)}
                        onChange={formik.handleChange}
                        error={formik.touched.conyuge_last_name && Boolean(formik.errors.conyuge_last_name)}
                        helperText={formik.touched.conyuge_last_name && formik.errors.conyuge_last_name}
                        value={formik.values.conyuge_last_name}
                        disabled={isLoading || formik.isSubmitting}
                      />
                    </FormControl>

                    <FormControl fullWidth>
                      <TextField
                        id="conyuge_last_name_2"
                        name="conyuge_last_name_2"
                        label="Apellido materno del conyuge"
                        margin="normal"
                        required
                        fullWidth
                        autoComplete="off"
                        onBlur={(e) => savePersonalInformation(e)}
                        onChange={formik.handleChange}
                        error={formik.touched.conyuge_last_name_2 && Boolean(formik.errors.conyuge_last_name_2)}
                        helperText={formik.touched.conyuge_last_name_2 && formik.errors.conyuge_last_name_2}
                        value={formik.values.conyuge_last_name_2}
                        disabled={isLoading || formik.isSubmitting}
                      />
                    </FormControl>
                  </React.Fragment>
                )}
              </React.Fragment>
            )}

            <Grid container spacing={1} my={1}>
              <Grid item xs={6} sm={6} md={6}>
                <Button
                  variant="blue_outlined"
                  type="button"
                  fullWidth
                  onClick={() => {
                    navigate("/producto/creditosimple/docs_aval");
                  }}
                  disabled={isLoading || formik.isSubmitting}
                  sx={{ padding: "0px", height: "100%" }}
                >
                  Regresar al inicio
                </Button>
              </Grid>

              <Grid item xs={6} sm={6} md={6}>
                <Button variant="default" type="submit" fullWidth disabled={!formik.isValid || formik.isSubmitting}>
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionAval;
